import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getPasswordConfirmedError,
	getPasswordConfirmedLoader,
	getResetCredentials,
	getStatus2FA,
} from 'redux/reducers/settings/selectors';
import { resetCredentials } from 'redux/reducers/settings/reducer';

const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_MAX_LENGTH = 50;

const PASSWORD_REQUIREMENTS: { label: string; test: (value: string) => boolean }[] = [
	{
		label: `Minimum ${PASSWORD_MIN_LENGTH} characters`,
		test: (v) => v.length >= PASSWORD_MIN_LENGTH,
	},
	{ label: 'At least 1 uppercase letter (A–Z)', test: (v) => /[A-Z]/.test(v) },
	{ label: 'At least 1 number (0–9)', test: (v) => /[0-9]/.test(v) },
	{ label: 'At least 1 special character (e.g., !@#$%^&*)', test: (v) => /[^A-Za-z0-9\s]/.test(v) },
];

export const ChangePassword: FC = () => {
	const dispatch = useDispatch();
	const status2FA = useSelector(getStatus2FA);

	const passwordConfirmedError = useSelector(getPasswordConfirmedError);
	const resetCredentialsIsOK = useSelector(getResetCredentials);
	const resetCredentialsIsLoading = useSelector(getPasswordConfirmedLoader);
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
	const handleMouseDownCurrentPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const [showNewPassword, setShowNewPassword] = useState(false);
	const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
	const handleMouseDownNewPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
	const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const validationPassword = (value: string) => {
		let error = '';
		if (value.length < PASSWORD_MIN_LENGTH) {
			error += `Password must be at least ${PASSWORD_MIN_LENGTH} characters. `;
		}
		if (value.length > PASSWORD_MAX_LENGTH) {
			error += `Password must be less than ${PASSWORD_MAX_LENGTH} characters. `;
		}
		if (!/[A-Z]/.test(value)) {
			error += 'Password must contain at least one uppercase letter. ';
		}
		if (!/[0-9]/.test(value)) {
			error += 'Password must contain at least one number. ';
		}
		if (!/[^A-Za-z0-9\s]/.test(value)) {
			error += 'Password must contain at least one special character. ';
		}
		return error;
	};

	const validationCode = (value: string) => {
		let error = '';
		if (value.length < 6) {
			error = 'Code must be 6 digits';
		}
		if (value.length > 6) {
			error = 'Code must be 6 digits';
		}
		if (!/^\d+$/.test(value)) {
			error = 'Code must be 6 digits';
		}
		if (+value < 0) {
			error = 'Code must be 6 positive digits';
		}

		return error;
	};

	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');

	const [newPasswordError, setNewPasswordError] = useState('');
	const [confirmPasswordError, setConfirmPasswordError] = useState('');

	const [code, setCode] = useState('');
	const [codeError, setCodeError] = useState('');
	const handleCode = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCode(event.target.value);
		setCodeError(validationCode(event.target.value));
	};

	const handleCurrentPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentPassword(event.target.value);
		// setCurrentPasswordError(validationPassword(event.target.value));
	};
	const handleNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		setNewPassword(event.target.value);
		setNewPasswordError(validationPassword(event.target.value));
		setConfirmPasswordError(event.target.value !== confirmPassword ? 'Password mismatch' : '');
	};

	const handleConfirmPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		setConfirmPassword(event.target.value);
		setConfirmPasswordError(event.target.value !== newPassword ? 'Password mismatch' : '');
	};

	const handleResetCredentials = () => {
		dispatch(
			resetCredentials({
				currentPassword,
				newPassword,
				totp: code,
			}),
		);
	};

	return (
		<Card
			sx={{
				// minHeight: '400px',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
			}}
		>
			<CardContent>
				<Typography
					color="primary"
					sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 3, alignContent: 'start' }}
				>
					Change password
				</Typography>

				{!resetCredentialsIsOK && (
					<>
						<FormControl
							variant="outlined"
							fullWidth
							required
							sx={{ marginBottom: 2 }}
							error={!!passwordConfirmedError}
						>
							<InputLabel htmlFor="outlined-adornment-password">Current password</InputLabel>
							<OutlinedInput
								sx={{ borderRadius: '12px' }}
								type={showCurrentPassword ? 'text' : 'password'}
								value={currentPassword}
								onChange={handleCurrentPassword}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowCurrentPassword}
											onMouseDown={handleMouseDownCurrentPassword}
											edge="end"
										>
											{showCurrentPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
								label="Current password"
							/>
							<FormHelperText>
								{passwordConfirmedError === 'invalid_grant (Invalid user credentials)'
									? 'There might be a typo in your password. Double-check and try again'
									: passwordConfirmedError}
							</FormHelperText>
						</FormControl>
						<FormControl
							variant="outlined"
							fullWidth
							required
							sx={{ marginBottom: 2 }}
							error={newPassword.length > 0 && !!newPasswordError}
						>
							<InputLabel htmlFor="outlined-adornment-password">New password</InputLabel>
							<OutlinedInput
								sx={{ borderRadius: '12px' }}
								type={showNewPassword ? 'text' : 'password'}
								value={newPassword}
								onChange={handleNewPassword}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowNewPassword}
											onMouseDown={handleMouseDownNewPassword}
											edge="end"
										>
											{showNewPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
								label="New password"
							/>
							<FormHelperText component="div">
								<Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
									Password requirements:
								</Typography>
								{PASSWORD_REQUIREMENTS.map(({ label, test }) => {
									const passed = test(newPassword);
									return (
										<Typography
											key={label}
											variant="caption"
											component="div"
											sx={{
												color: !newPassword.length
													? 'text.secondary'
													: passed
														? 'success.main'
														: 'error.main',
											}}
										>
											{passed ? '✓' : '•'} {label}
										</Typography>
									);
								})}
							</FormHelperText>
						</FormControl>
						<FormControl
							variant="outlined"
							fullWidth
							required
							sx={{ marginBottom: 2 }}
							error={confirmPassword.length > 0 && newPassword !== confirmPassword}
						>
							<InputLabel htmlFor="outlined-adornment-password">Confirm password</InputLabel>
							<OutlinedInput
								sx={{ borderRadius: '12px' }}
								type={showConfirmPassword ? 'text' : 'password'}
								value={confirmPassword}
								onChange={handleConfirmPassword}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowConfirmPassword}
											onMouseDown={handleMouseDownConfirmPassword}
											edge="end"
										>
											{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
								label="Confirm password"
							/>
							<FormHelperText>{!!confirmPassword.length && confirmPasswordError}</FormHelperText>
						</FormControl>
						{status2FA?.enabled && (
							<FormControl
								variant="outlined"
								fullWidth
								required
								error={!!codeError || !!passwordConfirmedError}
							>
								<InputLabel htmlFor="outlined-adornment-password">One-time 6-digit code</InputLabel>
								<OutlinedInput
									sx={{ borderRadius: '12px' }}
									type="number"
									value={code}
									onChange={handleCode}
									label="One-time 6-digit code"
								/>
								<FormHelperText>{codeError || passwordConfirmedError}</FormHelperText>
							</FormControl>
						)}
					</>
				)}
				{resetCredentialsIsOK && (
					<Typography
						variant="h6"
						sx={{ fontWeight: 'bold', alignContent: 'start', color: 'green' }}
						color="success"
					>
						Password changed successfully!
					</Typography>
				)}
			</CardContent>
			<CardActions sx={{ display: 'flex', justifyContent: 'end' }}>
				{!resetCredentialsIsOK && (
					<Button
						variant="contained"
						color="primary"
						sx={{ alignContent: 'end', display: 'inline', borderRadius: '12px' }}
						onClick={handleResetCredentials}
						disabled={
							currentPassword.length < 3 ||
							!newPassword.length ||
							(status2FA?.enabled && code.length < 6) ||
							!!newPasswordError ||
							!!confirmPasswordError ||
							!!codeError
						}
					>
						Change password
					</Button>
				)}
			</CardActions>
		</Card>
	);
};
